/**
 * @file Provides the confirmed add and delete actions for a transaction table row.
 */
import { App } from "antd";
import type { ModalFuncProps } from "antd";

import { confirmAction } from "@components/Page";

import type { EditableTransaction, TransactionActions } from "./types";

const ACTION_ADD = "ADD";
const ACTION_DELETE = "DELETE";

type ActionName = typeof ACTION_ADD | typeof ACTION_DELETE;

type ActionConfig = {
  type: ModalFuncProps["okType"];
  title: string;
  action: string;
  content: string;
  templateDisabledContent?: string;
};

const ACTIONS_MAP: Record<ActionName, ActionConfig> = {
  [ACTION_ADD]: {
    type: "primary",
    title: "Confirm Investment Funding?",
    action: "Confirm",
    content: `By clicking "Confirm" below, the funding status will change. An
email notification will be sent to the investor.`,
    templateDisabledContent: `By clicking "Confirm" below, the funding status
will change. No email notification will be sent to the investor.`,
  },
  [ACTION_DELETE]: {
    type: "danger",
    title: "Remove Funding Transaction",
    action: "Remove",
    content: "Removes funding transaction from investment, updates investment funding status.",
  },
};

type Options = TransactionActions & {
  record: EditableTransaction;
};

/**
 * Provides add and delete transaction actions that ask the user to confirm first.
 */
const useInvestmentTransactionActions = ({
  record,
  addTransactionAction,
  deleteTransactionAction,
  isInvestmentFundedTemplateEnabled,
}: Options) => {
  const { modal } = App.useApp();

  /**
   * Opens a confirmation modal for the add or delete action and runs it on confirm.
   */
  const action = (actionName: ActionName) => {
    const {
      templateDisabledContent,
      content: templateEnabledContent,
    } = ACTIONS_MAP[actionName];

    let content = templateEnabledContent;

    const isAddAction = actionName === ACTION_ADD;

    if (isAddAction) {
      content = isInvestmentFundedTemplateEnabled()
        ? templateEnabledContent
        : templateDisabledContent;
    }

    return confirmAction(
      modal,
      ACTIONS_MAP[actionName].action,
      ACTIONS_MAP[actionName].title,
      content,
      () => {
        if (actionName === ACTION_ADD) {
          return addTransactionAction();
        }

        return deleteTransactionAction(record);
      },
      ACTIONS_MAP[actionName].type,
    );
  };

  /**
   * Confirms and runs the add-transaction action.
   */
  const addTransaction = () => action(ACTION_ADD);

  /**
   * Confirms and runs the delete-transaction action.
   */
  const deleteTransaction = () => action(ACTION_DELETE);

  return {
    addTransaction,
    deleteTransaction,
  };
};

export default useInvestmentTransactionActions;
